import { Input } from "@headlessui/react";
import { Custom, GetUsersBoResponse } from "@/types/api/api-types";
import DateRangePicker, { DateRange } from "rsuite/DateRangePicker";
import "rsuite/DateRangePicker/styles/index.css";
import frFR from "rsuite/locales/fr_FR";
import "@/components/shared/daterange.style.css";
import { StyledSelectWoController } from "@/components/styled/StyledSelectWoController";
import { predefinedRanges } from "@/lib/predefinedRanges";
import { useState, useMemo } from "react";
import { Search, ListFilter } from "lucide-react";
import { useExpeditionFilters } from "./useExpeditionFilters.reducer";
import { CountryCombobox } from "../ui/customs/country-combobox";
import { toEndOfDay, toStartOfDay } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type FiltersProps = {
  filters: ReturnType<typeof useExpeditionFilters>["filters"];
  actions: ReturnType<typeof useExpeditionFilters>["actions"];
  customs: Custom[];
  users: GetUsersBoResponse;
};

export const FiltersPendingExpeditions = ({ filters, actions, users }: FiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  const [isVisible, setIsVisible] = useState(false);

  const dateRange: DateRange | null = useMemo(() => {
    if (filters.dateRange && filters.dateRange.length === 2) {
      return [new Date(filters.dateRange[0]), new Date(filters.dateRange[1])];
    }
    return null;
  }, [filters.dateRange]);

  const handleDateRangeChange = (newRange: DateRange | null) => {
    if (newRange && newRange.length === 2) {
      const start = toStartOfDay(newRange[0]);
      const end = toEndOfDay(newRange[1]);
      actions.setDateRange([start, end]);
    } else {
      actions.setDateRange(null);
    }
  };

  return (
    <div className="flex w-full flex-col justify-between gap-2 lg:flex-row">
      <div className="hidden w-full gap-2 lg:grid lg:grid-cols-4">
        <div className="flex flex-row items-center gap-3 rounded-lg border border-solid border-gray-300 bg-white px-3 py-2">
          <Search />
          <Input
            value={filters.search}
            onChange={(e) => actions.setSearch(e.target.value)}
            className="w-full text-sm font-medium placeholder:text-[#94A3B8] focus:outline-hidden"
            placeholder={t("pendingShipmentsFilters.searchPlaceholder")}
          />
        </div>
        <CountryCombobox
          value={filters.country}
          onValueChange={(e) => actions.setCountry(e)}
          placeholder={t("pendingShipmentsFilters.countryPlaceholder")}
          clearable
          className="h-12"
        />
        <DateRangePicker
          placeholder={t("pendingShipmentsFilters.dateRangePlaceholder")}
          ranges={predefinedRanges}
          showOneCalendar
          locale={frFR.Calendar}
          value={dateRange}
          onChange={handleDateRangeChange}
          format="dd/MM/yyyy"
          appearance="subtle"
          editable={false}
          character={t("pendingShipmentsFilters.dateRangeSeparator")}
          className="flex h-full w-full min-w-[200px] flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
          size="lg"
        />
        <StyledSelectWoController
          placeholder={t("pendingShipmentsFilters.userPlaceholder")}
          isClearable={true}
          options={users.map((user) => ({
            value: user.id,
            label: user.firstName + " " + user.lastName,
          }))}
          onChange={(e) => actions.setUserId(e?.value || "")}
          className="w-full"
          value={filters.userId}
        />
      </div>
      <div className="flex w-full flex-col justify-between gap-3 lg:hidden lg:flex-row">
        <div className="flex w-full flex-row items-stretch gap-2 xl:max-w-[40%]">
          <div className="flex max-w-[300px] min-w-[200px] flex-row items-center gap-3 rounded-lg border border-solid border-gray-300 bg-white px-3 py-2">
            <Search />
            <Input
              value={filters.search}
              onChange={(e) => actions.setSearch(e.target.value)}
              className="w-full text-sm font-medium placeholder:text-[#94A3B8] focus:outline-hidden"
              placeholder={t("pendingShipmentsFilters.searchPlaceholder")}
            />
          </div>
          <button
            className="block rounded-lg border border-gray-300 bg-white p-3 lg:hidden"
            onClick={() => setIsVisible(!isVisible)}
          >
            <ListFilter />
          </button>
        </div>
        {isVisible && (
          <div className="flex w-full flex-col gap-2 lg:hidden">
            <CountryCombobox
              value={filters.country}
              onValueChange={(e) => actions.setCountry(e)}
              placeholder={t("pendingShipmentsFilters.countryPlaceholder")}
              clearable
              className="h-12"
            />
            <DateRangePicker
              placeholder={t("pendingShipmentsFilters.dateRangePlaceholder")}
              ranges={predefinedRanges}
              showOneCalendar
              locale={frFR.Calendar}
              onChange={handleDateRangeChange}
              value={dateRange}
              format="dd/MM/yyyy"
              appearance="subtle"
              editable={false}
              character={t("pendingShipmentsFilters.dateRangeSeparator")}
              className="flex w-full flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
              size="lg"
            />
            <StyledSelectWoController
              placeholder={t("pendingShipmentsFilters.userPlaceholder")}
              isClearable={true}
              options={users.map((user) => ({
                value: user.id,
                label: user.firstName + " " + user.lastName,
              }))}
              onChange={(e) => actions.setUserId(e?.value || "")}
              className=""
              value={filters.userId}
            />
          </div>
        )}
      </div>
    </div>
  );
};
